import React, {useMemo, useState} from 'react';

import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {useGetAccountInfo, useGetAccountTeam} from 'src/use-cases';

import Button from '@pb/kit-component-button';
import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';


import {ConfirmDialog} from './confirm-dialog';
import {SelectEmailType} from './select-email-type.component';
import {SendWelcomeEmailSectionSkeleton} from './send-welcome-email-section.skeleton';

import type {EmailType} from 'src/openapi/admin/models/EmailType';
import type {Error} from 'src/openapi/admin/models/Error';

type Props = {
    isLoading: boolean,
    disabled: boolean,
    isSsoMandatory: boolean,
    userId: number,
    isSsoEnabled: boolean,
    accountId: number,
};

export const SendWelcomeEmailSection = (props: Props) => {
    const {disabled, isSsoMandatory, userId, isSsoEnabled, accountId} = props;
    const [welcomeEmailType, setWelcomeEmailType] = useState<EmailType | null>(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const {
        data: accountInfo,
        isLoading: isAccountLoading,
        loadingError: accountLoadingError,
    } = useGetAccountInfo({accountId});

    const {
        data: teamMembers,
        isLoading: isTeamMembersLoading,
        loadingError: teamMembersLoadingError,
    } = useGetAccountTeam({accountId});

    const isLoading: boolean = useMemo(() => (
        [props.isLoading, isAccountLoading, isTeamMembersLoading].some(Boolean)
    ), [isAccountLoading, isTeamMembersLoading, props.isLoading]);

    const loadingError: Error | null = useMemo(() => (
        [accountLoadingError, teamMembersLoadingError].find(Boolean) || null
    ), [accountLoadingError, teamMembersLoadingError]);

    return (
        <Skeleton shown={isLoading} view={<SendWelcomeEmailSectionSkeleton/>}>
            <ServerResponseHandler serverError={loadingError} sectionName="Welcome Email">
                <div>
                    <FormSection headline="Send Welcome Email">
                        <Group direction="v">
                            <SelectEmailType
                                emailType={welcomeEmailType}
                                setEmailType={setWelcomeEmailType}
                                teamMembers={teamMembers}
                                accountInfo={accountInfo}
                                ssoSettings={{ssoEnabled: isSsoEnabled, ssoMandatory: isSsoMandatory}}
                                disabled={disabled}
                            />
                            <Group>
                                <Button
                                    disabled={!welcomeEmailType}
                                    onClick={() => setShowConfirmDialog(true)}
                                    variant="Type.SECONDARY">
                                    Send
                                </Button>
                            </Group>
                        </Group>
                    </FormSection>
                    {welcomeEmailType ? (
                        <ConfirmDialog
                            emailType={welcomeEmailType}
                            teamMembers={teamMembers}
                            showConfirmDialog={showConfirmDialog}
                            userId={userId}
                            closeConfirmDialog={() => setShowConfirmDialog(false)}
                        />
                    ) : null}
                </div>
            </ServerResponseHandler>
        </Skeleton>
    );
};
